/** Draft generation — one auto-applied harmful card + 3 beneficial choices */

import type { PRNG } from '../rng.js';
import type { EnhancementLevels, MutationCard, MutationDraft } from '../types.js';
import { MutationType, REGISTRY } from './registry.js';
import { MAX_ENHANCEMENT_LEVEL, describeEnhancement, nextEnhancementLevel } from './levels.js';

const HARMFUL_TYPES: MutationType[] = [
  MutationType.RowInject,
  MutationType.GravityNudge,
  MutationType.ObstacleSpawn,
  MutationType.ColorShift,
];

export const BENEFICIAL_TYPES: readonly MutationType[] = [
  MutationType.RainbowRow,
  MutationType.ScoreFrenzy,
  MutationType.BubbleBomb,
  MutationType.SlowClockwork,
  MutationType.CascadeCoil,
  MutationType.WildcardGear,
  MutationType.NarrowSpectrum,
  MutationType.LubricatedBearings,
  MutationType.GovernorLock,
];

/** Number of beneficial choices offered per draft. */
export const DRAFT_OPTION_COUNT = 3;

function defToCard(type: MutationType): MutationCard {
  const def = REGISTRY[type];
  return {
    type,
    name: def.name,
    description: def.description,
    flavorText: def.flavorText,
    sentiment: def.sentiment,
  };
}

/**
 * Build the beneficial card for `type` at the level the player would reach
 * by picking it. Mk. V swaps in the cranked-up flavor line.
 */
export function beneficialCard(type: MutationType, levels: EnhancementLevels): MutationCard {
  const def = REGISTRY[type];
  const level = nextEnhancementLevel(levels, type);
  const isMax = level >= MAX_ENHANCEMENT_LEVEL;
  return {
    type,
    name: def.name,
    description: describeEnhancement(type, level, def.description),
    flavorText: isMax && def.maxFlavorText ? def.maxFlavorText : def.flavorText,
    sentiment: def.sentiment,
    level,
  };
}

/**
 * Generate a draft: one harmful card (auto-applied) plus 3 distinct
 * beneficial choices. Blueprints already at Mk. V stay in the pool at their
 * normal frequency and are re-offered at Mk. V, so taking the ultimate form
 * never shrinks the player's future choices.
 *
 * The harmful card has a probability of appearing based on turn number:
 * starts at 40% and scales up to 60% by turn 500+.
 * If no harmful card triggers, the harmful field is still set (to the
 * rolled card) but the engine decides whether to apply it based on the roll.
 *
 * `options` always holds DRAFT_OPTION_COUNT cards (the pool is larger than
 * that); the engine still guards against an empty hand defensively.
 */
export function generateDraft(
  rng: PRNG,
  turnNumber: number,
  levels: EnhancementLevels = {},
): { applied: boolean; draft: MutationDraft } {
  // Roll for whether a harmful card appears this draft
  const harmfulChance = 0.4 + Math.min(turnNumber / 500, 0.2);
  const roll = rng.next();
  const applied = roll < harmfulChance;

  // Pick the harmful card
  const harmful = defToCard(rng.pick(HARMFUL_TYPES));

  // Pick 3 distinct beneficial cards (Mk. V blueprints included)
  let available: MutationType[] = [...BENEFICIAL_TYPES];
  const options: MutationCard[] = [];
  while (options.length < DRAFT_OPTION_COUNT && available.length > 0) {
    const chosen = rng.pick(available);
    available = available.filter(t => t !== chosen);
    options.push(beneficialCard(chosen, levels));
  }

  return { applied, draft: { harmful, options } };
}
